import { PLAYER } from '../core/config';
import { layoutToWorld, worldToLayout } from '../core/coords';
import { createControlState } from '../controls/controller';
import type { ControlEnv, ControlState } from '../controls/types';
import type { Vec2 } from '../geo/types';

const SPAWN_RADIUS_M = 8;
const SPAWN_SEPARATION_M = 1.2;
const SPAWN_ATTEMPTS = 128;

export function createMultiplayerControlState(env: ControlEnv): ControlState {
  return createControlState(multiplayerSpawn(env), 'walk', env);
}

function clearance(point: Vec2, occupied: readonly Vec2[]): number {
  return occupied.reduce(
    (gap, other) => Math.min(gap, Math.hypot(point[0] - other[0], point[1] - other[1])),
    Infinity
  );
}

/*
 * Each browser needs its own arrival position; the city's fixed world seed would
 * put every visitor in the same place. Rejection preserves the full body clearance
 * against real buildings and water instead of snapping many samples to one edge.
 */
export function multiplayerSpawn(
  env: Pick<ControlEnv, 'spawn' | 'collision'>,
  occupied: readonly Vec2[] = [],
  random: () => number = Math.random
): Vec2 {
  let best = env.spawn;
  let bestGap = clearance(best, occupied);
  for (let attempt = 0; attempt < SPAWN_ATTEMPTS; attempt++) {
    const angle = random() * Math.PI * 2;
    const radius = Math.sqrt(1.5 ** 2 + random() * (SPAWN_RADIUS_M ** 2 - 1.5 ** 2));
    const point: Vec2 = [
      env.spawn[0] + Math.cos(angle) * radius,
      env.spawn[1] + Math.sin(angle) * radius,
    ];
    if (!env.collision.contains(point)) continue;
    const gap = clearance(point, occupied);
    if (gap >= SPAWN_SEPARATION_M) return point;
    if (gap > bestGap) {
      best = point;
      bestGap = gap;
    }
  }
  return best;
}

/*
 * Nearby visitors are known only after the first shared snapshot. Resolve an
 * occupied arrival then, without moving a visitor who has already left the Dam.
 */
export function separateMultiplayerEntry(
  state: ControlState,
  env: ControlEnv,
  occupied: readonly Vec2[],
  random: () => number = Math.random
): ControlState {
  const here = worldToLayout(state.pose.position);
  if (
    state.mode !== 'walk' ||
    Math.hypot(here[0] - env.spawn[0], here[1] - env.spawn[1]) > SPAWN_RADIUS_M ||
    clearance(here, occupied) >= SPAWN_SEPARATION_M
  )
    return state;
  const point = multiplayerSpawn(env, occupied, random);
  return {
    ...state,
    pose: {
      ...state.pose,
      position: layoutToWorld(point, env.groundHeightAt(point) + PLAYER.eyeHeight),
    },
    velocity: [0, 0, 0],
  };
}
